// src/features/clients/components/ClientManagement/index.jsx
import React, { useState, useMemo } from 'react';

import useClients                        from '../../hooks/useClients';
import { useAuth }                       from '../../../auth/context/AuthContext';
import { resetClientPassword }           from '../../../../api';

import Button                 from '../../../../shared/components/ui/Button';
import LoadingSpinner         from '../../../../shared/components/ui/LoadingSpinner';
import EmptyState             from '../../../../shared/components/ui/EmptyState';
import Modal, { ModalFooter } from '../../../../shared/components/ui/Modal';

import styles from './ClientManagement.module.css';

const AVATAR_COLORS = [
  { bg: '#E6F1FB', fg: '#185FA5' },
  { bg: '#E1F5EE', fg: '#085041' },
  { bg: '#EEEDFE', fg: '#534AB7' },
  { bg: '#FAEEDA', fg: '#854F0B' },
  { bg: '#FAECE7', fg: '#712B13' },
  { bg: '#FBEAF0', fg: '#72243E' },
];

// ─── أيقونة الجدول ──────────────────────────────────────────
const TableIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
    <rect x="1" y="1" width="14" height="14" rx="2"/>
    <path d="M1 5h14M1 9h14M1 13h14M5 1v14M11 1v14"/>
  </svg>
);

// ─── أيقونة البطاقات ────────────────────────────────────────
const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
    <rect x="1" y="1" width="6" height="6" rx="1.5"/>
    <rect x="9" y="1" width="6" height="6" rx="1.5"/>
    <rect x="1" y="9" width="6" height="6" rx="1.5"/>
    <rect x="9" y="9" width="6" height="6" rx="1.5"/>
  </svg>
);

const ClientManagement = () => {
  const { hasPermission }                                  = useAuth();
  const { clients, loading, error, addClient, editClient } = useClients();

  const [viewMode,   setViewMode]   = useState('table'); // 'table' | 'cards'
  const [searchText, setSearchText] = useState('');

  const filteredClients = useMemo(() => {
    if (!searchText.trim()) return clients;
    const q = searchText.toLowerCase();
    return clients.filter(c =>
      c.name?.toLowerCase().includes(q) || c.phone?.includes(q)
    );
  }, [clients, searchText]);

  // مودال إضافة/تعديل
  const [modalOpen,     setModalOpen]     = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [submitting,    setSubmitting]    = useState(false);
  const [formError,     setFormError]     = useState('');
  const [formValues,    setFormValues]    = useState({ name: '', phone: '' });

  // مودال كلمة المرور
  const [passModalOpen,  setPassModalOpen]  = useState(false);
  const [passClient,     setPassClient]     = useState(null);
  const [passValues,     setPassValues]     = useState({ new_password: '', confirm_password: '' });
  const [passSubmitting, setPassSubmitting] = useState(false);
  const [passError,      setPassError]      = useState('');
  const [passSuccess,    setPassSuccess]    = useState('');

  const openAdd = () => {
    setEditingClient(null);
    setFormValues({ name: '', phone: '' });
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setFormValues({ name: client.name, phone: client.phone ?? '' });
    setFormError('');
    setModalOpen(true);
  };

  const openPassword = (client) => {
    setPassClient(client);
    setPassValues({ new_password: '', confirm_password: '' });
    setPassError('');
    setPassSuccess('');
    setPassModalOpen(true);
  };

  const handleSave = async () => {
    if (!formValues.name.trim()) { setFormError('اسم العميل مطلوب.'); return; }
    setSubmitting(true); setFormError('');
    try {
      const payload = { name: formValues.name.trim(), phone: formValues.phone.trim() || null };
      editingClient ? await editClient(editingClient.id, payload) : await addClient(payload);
      setModalOpen(false);
    } catch (err) {
      const data = err.response?.data;
      setFormError(data?.phone?.[0] ?? data?.name?.[0] ?? 'حدث خطأ أثناء الحفظ.');
    } finally { setSubmitting(false); }
  };

  const handleSavePassword = async () => {
    setPassError(''); setPassSuccess('');
    if (!passValues.new_password) { setPassError('يرجى إدخال كلمة المرور الجديدة.'); return; }
    if (passValues.new_password.length < 6) { setPassError('كلمة المرور يجب أن تكون 6 أحرف على الأقل.'); return; }
    if (passValues.new_password !== passValues.confirm_password) { setPassError('كلمتا المرور غير متطابقتين.'); return; }
    setPassSubmitting(true);
    try {
      await resetClientPassword(passClient.id, { new_password: passValues.new_password });
      setPassSuccess('تم تغيير كلمة المرور بنجاح.');
      setPassValues({ new_password: '', confirm_password: '' });
    } catch (err) {
      const data = err.response?.data;
      setPassError(data?.new_password?.[0] || data?.detail || 'فشل في تغيير كلمة المرور.');
    } finally { setPassSubmitting(false); }
  };

  if (loading) return <LoadingSpinner message="جاري تحميل العملاء..." />;

  return (
    <div className={styles.container}>

      {/* الهيدر */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>قائمة العملاء</h1>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
            {clients.length} عميل إجمالاً
            {searchText && filteredClients.length !== clients.length && ` · ${filteredClients.length} نتيجة`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* زر تبديل العرض */}
          <div className={styles.viewToggle}>
            <button
              type="button"
              className={`${styles.toggleBtn} ${viewMode === 'table' ? styles.toggleActive : ''}`}
              onClick={() => setViewMode('table')}
              title="عرض جدول"
            >
              <TableIcon />
            </button>
            <button
              type="button"
              className={`${styles.toggleBtn} ${viewMode === 'cards' ? styles.toggleActive : ''}`}
              onClick={() => setViewMode('cards')}
              title="عرض بطاقات"
            >
              <GridIcon />
            </button>
          </div>
          {hasPermission('add_client') && (
            <Button variant="primary" onClick={openAdd}>+ إضافة عميل</Button>
          )}
        </div>
      </div>

      {error && <p className={styles.errorMsg}>{error}</p>}

      {/* شريط البحث */}
      <div className={styles.searchBar}>
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--text-muted)' }}>
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="ابحث بالاسم أو رقم الهاتف..."
          className={styles.searchInput}
        />
        {searchText && (
          <button type="button" onClick={() => setSearchText('')} className={styles.searchClear}>×</button>
        )}
      </div>

      {/* المحتوى */}
      {filteredClients.length === 0 ? (
        <EmptyState
          title={searchText ? 'لا توجد نتائج' : 'لا يوجد عملاء'}
          message={searchText ? `لا يوجد عميل يطابق "${searchText}"` : 'ابدأ بإضافة عميل جديد.'}
          actionLabel={!searchText && hasPermission('add_client') ? '+ إضافة عميل' : undefined}
          onAction={!searchText ? openAdd : undefined}
        />
      ) : viewMode === 'table' ? (

        /* ══ عرض الجدول ══ */
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>#</th>
                <th>العميل</th>
                <th>رقم الهاتف</th>
                <th>الحساب</th>
                <th style={{ width: '160px' }}>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client, idx) => {
                const c = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                return (
                  <tr key={client.id} className={styles.tableRow}>
                    <td className={styles.tdCenter}>{idx + 1}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                          background: c.bg, color: c.fg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', fontWeight: 600,
                        }}>
                          {(client.name || '؟').slice(0, 2)}
                        </div>
                        <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{client.name}</span>
                      </div>
                    </td>
                    <td>
                      {client.phone
                        ? <span style={{ direction: 'ltr', display: 'inline-block', color: 'var(--text-secondary)', fontSize: '13px' }}>{client.phone}</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>—</span>
                      }
                    </td>
                    <td>
                      {client.has_account
                        ? <span className={styles.badgeAccount}>لديه حساب</span>
                        : <span className={styles.badgeNoAccount}>بدون حساب</span>
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {hasPermission('change_client') && (
                          <Button variant="warning" size="sm" onClick={() => openEdit(client)}>تعديل</Button>
                        )}
                        {hasPermission('change_client') && client.phone && (
                          <Button variant="secondary" size="sm" onClick={() => openPassword(client)}>🔑</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      ) : (

        /* ══ عرض البطاقات ══ */
        <div className={styles.cardGrid}>
          {filteredClients.map((client, idx) => {
            const c = AVATAR_COLORS[idx % AVATAR_COLORS.length];
            return (
              <div key={client.id} className={styles.clientCard}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: c.bg, color: c.fg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '15px', fontWeight: 600,
                }}>
                  {(client.name || '؟').slice(0, 2)}
                </div>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-heading)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</div>
                  {client.phone
                    ? <div style={{ fontSize: '12px', color: 'var(--text-muted)', direction: 'ltr', textAlign: 'right' }}>{client.phone}</div>
                    : <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>بدون هاتف</div>
                  }
                  {client.has_account && (
                    <span className={styles.badgeAccount} style={{ marginTop: '3px', display: 'inline-block' }}>لديه حساب</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  {hasPermission('change_client') && (
                    <Button variant="warning" size="sm" onClick={() => openEdit(client)}>تعديل</Button>
                  )}
                  {hasPermission('change_client') && client.phone && (
                    <Button variant="secondary" size="sm" onClick={() => openPassword(client)}>🔑</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* مودال الإضافة/التعديل */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editingClient ? `تعديل: ${editingClient.name}` : 'إضافة عميل جديد'}
        footer={<ModalFooter onClose={() => setModalOpen(false)} onSave={handleSave} saveLabel={editingClient ? 'حفظ التعديلات' : 'حفظ العميل'} loading={submitting} />}>
        <div className={styles.form}>
          <div className={styles.field}>
            <label>اسم العميل *</label>
            <input value={formValues.name} onChange={e => setFormValues(p => ({ ...p, name: e.target.value }))} placeholder="أدخل اسم العميل" autoFocus />
          </div>
          <div className={styles.field}>
            <label>رقم الهاتف</label>
            <input value={formValues.phone} onChange={e => setFormValues(p => ({ ...p, phone: e.target.value }))} placeholder="اختياري" />
          </div>
          {formError && <p className={styles.formError}>{formError}</p>}
        </div>
      </Modal>

      {/* مودال كلمة المرور */}
      <Modal isOpen={passModalOpen} onClose={() => setPassModalOpen(false)}
        title={`🔑 تغيير كلمة مرور: ${passClient?.name}`}
        footer={<ModalFooter onClose={() => setPassModalOpen(false)} onSave={handleSavePassword} saveLabel="تغيير كلمة المرور" loading={passSubmitting} />}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ padding: '10px 14px', background: 'var(--color-warning-subtle)', border: '1px solid var(--color-warning-muted)', borderRight: '3px solid var(--color-warning)', borderRadius: 'var(--radius-md)', fontSize: '12px', color: 'var(--color-warning-text)', fontWeight: 600 }}>
            ⚠️ سيتم تغيير كلمة مرور حساب <strong>{passClient?.name}</strong>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>كلمة المرور الجديدة *</label>
            <input type="password" value={passValues.new_password} onChange={e => setPassValues(p => ({ ...p, new_password: e.target.value }))} placeholder="أدخل كلمة المرور الجديدة" disabled={passSubmitting} dir="ltr" />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>تأكيد كلمة المرور *</label>
            <input type="password" value={passValues.confirm_password} onChange={e => setPassValues(p => ({ ...p, confirm_password: e.target.value }))} placeholder="أعد إدخال كلمة المرور" disabled={passSubmitting} dir="ltr" />
            {passValues.new_password && passValues.confirm_password && (
              <small style={{ display: 'block', marginTop: '4px', fontSize: '12px', fontWeight: 600, color: passValues.new_password === passValues.confirm_password ? 'var(--color-success-text)' : 'var(--color-danger-text)' }}>
                {passValues.new_password === passValues.confirm_password ? '✓ متطابقتان' : '✗ غير متطابقتين'}
              </small>
            )}
          </div>
          {passError   && <p style={{ margin:0, padding:'10px 14px', background:'var(--color-danger-subtle)',  border:'0.5px solid var(--color-danger-border)',  borderRight:'3px solid var(--color-danger)',  borderRadius:'var(--radius-md)', color:'var(--color-danger-text)',  fontSize:'13px', fontWeight:600 }}>⚠️ {passError}</p>}
          {passSuccess && <p style={{ margin:0, padding:'10px 14px', background:'var(--color-success-subtle)', border:'0.5px solid var(--color-success-border)', borderRight:'3px solid var(--color-success)', borderRadius:'var(--radius-md)', color:'var(--color-success-text)', fontSize:'13px', fontWeight:600 }}>✓ {passSuccess}</p>}
        </div>
      </Modal>

    </div>
  );
};

export default ClientManagement;